// src/components/settings/AccountTab.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { settingsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

function AccountTab() {
    const { user, updateUser } = useAuth();
    const queryClient = useQueryClient();

    const [formData, setFormData] = useState({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
    });

    const { data: accountData } = useQuery('accountSettings', settingsAPI.getAccount);

    const updateMutation = useMutation(settingsAPI.updateAccount, {
        onSuccess: (data) => {
            queryClient.invalidateQueries('accountSettings');
            updateUser(data.data);
            toast.success('Account updated successfully!');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to update account');
        },
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        updateMutation.mutate(formData);
    };

    const handleChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium text-gray-900">Account Settings</h3>
                <p className="mt-1 text-sm text-gray-600">
                    Update your account information and preferences
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="text-md font-medium text-gray-900 mb-4">Personal Information</h4>

                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div>
                            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                                First name
                            </label>
                            <input
                                type="text"
                                name="firstName"
                                id="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                        </div>

                        <div>
                            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                                Last name
                            </label>
                            <input
                                type="text"
                                name="lastName"
                                id="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                        </div>

                        <div className="sm:col-span-2">
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                Email address
                            </label>
                            <input
                                type="email"
                                name="email"
                                id="email"
                                value={formData.email}
                                disabled
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500 sm:text-sm"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                Email cannot be changed. Contact support if needed.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="text-md font-medium text-gray-900 mb-4">Plan & Usage</h4>

                    <div className="space-y-4">
                        <div className="flex justify-between">
                            <span className="text-sm text-gray-700">Current Plan</span>
                            <span className="text-sm font-medium text-gray-900 capitalize">
                                {user?.plan || 'Free'}
                            </span>
                        </div>

                        <div className="flex justify-between">
                            <span className="text-sm text-gray-700">Posts Generated</span>
                            <span className="text-sm font-medium text-gray-900">
                                {user?.usageStats?.postsGenerated || 0}
                            </span>
                        </div>

                        <div className="flex justify-between">
                            <span className="text-sm text-gray-700">Posts Published</span>
                            <span className="text-sm font-medium text-gray-900">
                                {user?.usageStats?.postsPublished || 0}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={updateMutation.isLoading}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                        {updateMutation.isLoading ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default AccountTab;